import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { gengql } from '@/__generated__';
import { GroupRole, InvitationCollectionType } from '@/__generated__/graphql';
import { APIError } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { Viewer } from '@/core/viewer';
import { debounce, normalizeSpace } from '@/utils';

import type { CurrentOwner } from './TransferOwnershipDialog';
import { ActivityGuardError, handleMutationError } from './handleMutationError';
import { useOwnershipActions } from './useOwnershipActions';

// Re-exported so existing call sites (and tests) keep working.
export { extractActivityGuardError } from './handleMutationError';

const FETCH_TEAM_MEMBERS = gengql(/* GraphQL */ `
  query getTeamMembers(
    $groupId: String!
    $limit: Int!
    $page: Int
    $name: String
    $states: [GroupMemberState]
  ) {
    groupMembers(group_id: $groupId, limit: $limit, page: $page, name: $name, states: $states) {
      edges {
        node {
          id
          role
          email
          hasUpcomingConsultations
          hasActiveRequests
          user {
            id
            html_url
            picture_url
            name
            first_name
            last_name
            city
            country
          }
        }
      }
      pageInfo {
        total
        lastPage
      }
    }
  }
`);

const ADD_TEAM_MEMBER = gengql(/* GraphQL */ `
  mutation addTeamMember($groupId: String!, $userId: String!, $role: GroupRole!) {
    addGroupMember(group_id: $groupId, user_id: $userId, role: $role) {
      id
      email
      role
      user {
        id
        first_name
        last_name
        name
        html_url
        picture_url
        city
        country
      }
    }
  }
`);

const INVITE_USER = gengql(/* GraphQL */ `
  mutation inviteUser(
    $email: String
    $profileId: String
    $firstName: String
    $lastName: String
    $collectionType: InvitationCollectionType!
    $collectionId: String!
    $role: String!
    $teamNote: String
    $invitationMessage: String
  ) {
    inviteUser(
      email: $email
      profile_id: $profileId
      first_name: $firstName
      last_name: $lastName
      collection_type: $collectionType
      collection_id: $collectionId
      role: $role
      team_note: $teamNote
      invitation_message: $invitationMessage
    ) {
      id
      role
      email
    }
  }
`);

const UPDATE_TEAM_MEMBER = gengql(/* GraphQL */ `
  mutation updateTeamMember($id: String!, $role: GroupRole!) {
    updateGroupMember(id: $id, role: $role) {
      id
      email
      user {
        id
      }
    }
  }
`);

const REMOVE_TEAM_MEMBER = gengql(/* GraphQL */ `
  mutation removeTeamMember($id: String!) {
    removeGroupMember(id: $id) {
      id
      email
      user {
        id
      }
    }
  }
`);

const TRANSFER_OWNERSHIP = gengql(/* GraphQL */ `
  mutation transferOwnership($groupId: String!, $memberId: String!, $callerRole: GroupRole) {
    transferGroupOwnership(group_id: $groupId, member_id: $memberId, caller_role: $callerRole) {
      id
      role
    }
  }
`);

const DEFAULT_FETCH_LIMIT = 10;

type Invitation = {
  email: string;
  profileId: string;
  firstName: string;
  lastName: string;
  collectionType: InvitationCollectionType;
  collectionId: string;
  role: string;
  teamNote: string;
  invitationMessage: string;
};

export function useTeamMembers({
  viewer,
  groupId,
  team,
  notify,
  fetchGroup,
}: {
  viewer: Viewer;
  groupId: string;
  team: any;
  notify: (...args: any[]) => void;
  fetchGroup: (
    id: string,
    opts?: { subscription?: boolean }
  ) => Promise<{ has_owner?: boolean | null; billing_account?: any } | undefined>;
}) {
  const { members_awaiting: awaitingPage } = team;
  const { name, billing_account: billingAccount, has_owner: hasOwner } = team;
  const isSMB = billingAccount?.isSMBAccount;
  const seatAllocation = billingAccount?.seatAllocation;

  const [addOpen, setAddOpen] = useState(false);
  const [addTeamMemberLoading, setAddTeamMemberLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [nameSearch, setNameSearch] = useState<string | null>(null);

  const [activityGuardError, setActivityGuardError] = useState<ActivityGuardError | null>(null);

  const gqlClient = useApolloClient();
  const addTeamMember = async (gId: string, { userId, role }: any) => {
    return gqlClient.mutate({
      mutation: ADD_TEAM_MEMBER,
      variables: { groupId: gId, userId, role },
    });
  };
  const inviteUser = async (invitation: Invitation) => {
    return gqlClient.mutate({
      mutation: INVITE_USER,
      variables: invitation,
    });
  };
  const [updateTeamMember, { loading: updateTeamMemberLoading, error: updateTeamMemberError }] =
    useMutation(UPDATE_TEAM_MEMBER, {
      refetchQueries: ['getTeamMembers'],
      awaitRefetchQueries: false,
    });
  const [removeTeamMember, { loading: removeTeamMemberLoading, error: removeTeamMemberError }] =
    useMutation(REMOVE_TEAM_MEMBER, { refetchQueries: ['getTeamMembers'] });
  const [transferOwnership, { loading: transferLoading }] = useMutation(TRANSFER_OWNERSHIP, {
    refetchQueries: ['getTeamMembers'],
    awaitRefetchQueries: false,
  });

  const refreshSeatAllocation = useCallback(() => {
    if (isSMB) fetchGroup(groupId, { subscription: true });
  }, [isSMB, fetchGroup, groupId]);

  const {
    data,
    loading: fetchTeamMembersLoading,
    refetch,
    error: fetchTeamMembersError,
  } = useQuery(FETCH_TEAM_MEMBERS, {
    notifyOnNetworkStatusChange: true,
    variables: { groupId, page: 1, name, limit: DEFAULT_FETCH_LIMIT },
  });

  const members = useMemo(
    () => data?.groupMembers || { edges: [], pageInfo: {} },
    [data?.groupMembers]
  );
  const totalMembers = members.pageInfo?.total || 0;

  useEffect(() => {
    [fetchTeamMembersError, updateTeamMemberError, removeTeamMemberError].forEach((e) => {
      if (e && e instanceof APIError) {
        e.rawError.map((error: any) => notify(error.message));
      }
    });
  }, [fetchTeamMembersError, updateTeamMemberError, removeTeamMemberError, notify]);

  const isLoading =
    fetchTeamMembersLoading ||
    addTeamMemberLoading ||
    updateTeamMemberLoading ||
    removeTeamMemberLoading ||
    transferLoading;

  if (!isLoading && page > 1 && !members.edges.length) {
    setPage(page - 1);
  }

  const fetchPageMembers = useCallback(
    ({ name: n = nameSearch } = {}) => {
      refetch({ page, name: n });
    },
    [refetch, page, nameSearch]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((n: any) => {
      fetchPageMembers({ name: n });
    }, 500),
    [fetchPageMembers]
  );

  useEffect(() => {
    fetchPageMembers({ name: nameSearch });
  }, [fetchPageMembers, nameSearch]);

  useEffect(() => {
    if (nameSearch === null) return;
    debounceSearch(nameSearch);
  }, [nameSearch, debounceSearch]);

  const ownership = useOwnershipActions({
    groupId,
    team,
    members,
    notify,
    fetchGroup,
    refreshSeatAllocation,
    setActivityGuardError,
    updateTeamMember,
    transferOwnership,
    transferLoading,
  });

  const removeTeamMemberAndFetch = async (id: string) => {
    try {
      await removeTeamMember({ variables: { id } });
      refreshSeatAllocation();
    } catch (err: any) {
      await handleMutationError(
        err,
        {
          onGuard: setActivityGuardError,
          codeMap: {
            [ERROR_CODES.TEAM_CANNOT_REMOVE_LAST_OWNER]: () =>
              notify('Owner seats must be transferred before removal.', 'error'),
            [ERROR_CODES.BILLING_SUBSCRIPTION_INACTIVE]: () =>
              notify('Your subscription is inactive — reactivate to manage members.', 'error'),
          },
          fallback: 'Failed to remove member',
        },
        notify
      );
    }
  };

  const updateTeamMemberAndFetch = async (id: string, role: GroupRole) => {
    // Promotion to Owner is delegated to useOwnershipActions, which routes
    // between initial-assign / SMB confirmation dialog / direct transfer
    // based on team.has_owner and team.billing_account.isSMBAccount.
    if (role === GroupRole.Owner) {
      const member = members.edges.find(({ node: m }: any) => m.id === id)?.node;
      if (!member) return;
      await ownership.promoteToOwner(member);
      return;
    }

    try {
      await updateTeamMember({ variables: { id, role } });
      refreshSeatAllocation();
    } catch (err: any) {
      await handleMutationError(
        err,
        {
          onGuard: setActivityGuardError,
          codeMap: {
            [ERROR_CODES.TEAM_OWNER_CANNOT_CHANGE_ROLE]: () =>
              notify('The team Owner must transfer ownership before changing roles.', 'error'),
            [ERROR_CODES.BILLING_SUBSCRIPTION_INACTIVE]: () =>
              notify('Your subscription is inactive — reactivate to manage members.', 'error'),
          },
          fallback: 'Failed to update member',
        },
        notify
      );
    }
  };

  const awaitingMembersData = useMemo(() => {
    if (!awaitingPage) return null;
    const awaiting = awaitingPage.edges.map((x: any) => x.node).filter((x: any) => x.user);
    if (!awaiting.length) return null;
    return awaiting;
  }, [awaitingPage]);

  const permissions = team.permissions || [];
  // `members` is a single page of at most DEFAULT_FETCH_LIMIT rows, narrowed
  // further by the member search box, so the viewer's own row is often absent
  // from it — always for staff viewing a team they don't belong to. That means
  // the viewer's role is *unresolved*, not Owner (ENG-5529). It stays local to
  // this hook and feeds only `currentOwner`, so no caller can mistake `null`
  // for "no access"; action gates come from `perms` below, which is built from
  // server-computed `team.permissions`.
  const viewerMember = members.edges.find(({ node: m }) => m.user?.id === viewer.id)?.node;
  const viewerRole: GroupRole | null = viewerMember?.role ?? null;

  // The transfer swaps seats: the promoted member takes Owner, and whoever
  // holds Owner today drops to the promoted member's old role. Naming that
  // person needs their row too, so it is subject to the same paging limits.
  const currentOwner: CurrentOwner =
    hasOwner === false
      ? { kind: 'none' }
      : viewerRole === GroupRole.Owner
        ? { kind: 'viewer' }
        : {
            kind: 'other',
            name:
              members.edges.find(({ node: m }) => m.role === GroupRole.Owner)?.node.user?.name ??
              undefined,
          };

  const memberIDs = members.edges.map(({ node: m }) => m.user?.id).filter((x: any) => x);
  const addUserFilter = (suggestionCandidate: any) => !memberIDs.includes(suggestionCandidate.id);

  const perms = {
    add: permissions.some((x: any) => x === 'add_member' || x === 'add_viewer'),
    update: {
      owner: permissions.some((x: any) => x === 'update_owner'),
      admin: permissions.some((x: any) => x === 'update_admin'),
      member: permissions.some((x: any) => x === 'update_member'),
      viewer: permissions.some((x: any) => x === 'update_viewer'),
    },
    remove: {
      owner: permissions.some((x: any) => x === 'remove_owner'),
      admin: permissions.some((x: any) => x === 'remove_admin'),
      member: permissions.some((x: any) => x === 'remove_member'),
      viewer: permissions.some((x: any) => x === 'remove_viewer'),
    },
    allowedRoles: permissions
      .filter((x: any) => x.startsWith('update_'))
      .map((x: any) => x.replace(/^update_/, '')),
  };

  const totalPages = members.pageInfo?.lastPage ?? 0;

  const canRemoveMember = (m: { role: keyof typeof perms.remove }) => {
    return perms.remove[m.role];
  };

  return {
    // State
    members,
    totalMembers,
    totalPages,
    page,
    setPage,
    nameSearch,
    setNameSearch,
    addOpen,
    setAddOpen,
    addTeamMemberLoading,
    setAddTeamMemberLoading,
    activityGuardError,
    setActivityGuardError,
    transferTarget: ownership.transferTarget,
    setTransferTarget: ownership.setTransferTarget,
    isLoading,
    transferLoading,

    // Data
    isSMB,
    seatAllocation,
    billingAccount,
    awaitingMembersData,
    viewerMember,
    currentOwner,
    perms,

    // Functions
    addTeamMember,
    inviteUser,
    updateTeamMember,
    updateTeamMemberAndFetch,
    removeTeamMemberAndFetch,
    handleTransferConfirm: ownership.handleTransferConfirm,
    refreshSeatAllocation,
    fetchPageMembers,
    refetch,
    addUserFilter,
    canRemoveMember,
    normalizeSpace,
  };
}
